import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Server, Plus, Pencil, Trash2, Activity, Loader2, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/lib/api';

const STATUS_COLORS = {
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  provisioning: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  maintenance: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  decommissioned: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const HEALTH_COLORS = { ok: 'text-emerald-400', fail: 'text-red-400' };

const ROLES = [
  { value: 'shared', label: 'Shared' },
  { value: 'priority-dedicated', label: 'Priority Dedicated' },
];

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'provisioning', label: 'Provisioning' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'decommissioned', label: 'Decommissioned' },
];

const SERVICE_FIELDS = {
  ml_api: [{ key: 'url', label: 'URL', type: 'text' }],
  qdrant: [{ key: 'url', label: 'URL', type: 'text' }],
  neo4j: [{ key: 'url', label: 'Bolt URL', type: 'text' }, { key: 'web_url', label: 'Web URL', type: 'text' }, { key: 'username', label: 'Username', type: 'password' }, { key: 'password', label: 'Password', type: 'password' }],
  elasticsearch: [{ key: 'url', label: 'URL', type: 'text' }],
  kafka: [{ key: 'brokers', label: 'Brokers', type: 'text' }],
  minio: [{ key: 'url', label: 'URL', type: 'text' }, { key: 'console_url', label: 'Console URL', type: 'text' }, { key: 'access_key', label: 'Access Key', type: 'password' }, { key: 'secret_key', label: 'Secret Key', type: 'password' }],
  redis_cache: [{ key: 'url', label: 'URL', type: 'text' }, { key: 'password', label: 'Password', type: 'password' }],
  redis_celery: [{ key: 'url', label: 'URL', type: 'text' }, { key: 'password', label: 'Password', type: 'password' }],
  lightpanda: [{ key: 'url', label: 'URL', type: 'text' }],
  vane: [{ key: 'url', label: 'URL', type: 'text' }],
};

export default function ServerManagement() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editServer, setEditServer] = useState(null);
  const [testing, setTesting] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/servers');
      setServers(res.data.servers || []);
    } catch (err) { toast.error('Failed to load servers'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleTest = async (serverId) => {
    setTesting(t => ({ ...t, [serverId]: 'loading' }));
    try {
      const res = await api.post(`/admin/servers/${serverId}/test-connection`);
      setTesting(t => ({ ...t, [serverId]: res.data.status }));
      if (res.data.status === 'ok') toast.success(`${serverId}: OK (${res.data.latency_ms}ms)`);
      else toast.error(`${serverId}: FAIL — ${res.data.error}`);
      load();
    } catch (err) {
      setTesting(t => ({ ...t, [serverId]: 'fail' }));
      toast.error(`Test failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDelete = async (serverId, customerCount) => {
    if (customerCount > 0) {
      toast.error(`Cannot delete: ${customerCount} customer(s) assigned. Reassign them first.`);
      return;
    }
    if (!window.confirm(`Delete server "${serverId}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/servers/${serverId}`);
      toast.success('Server deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const timeAgo = (ts) => {
    if (!ts) return 'Never';
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <div data-testid="server-management" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">GPU Servers</h2>
          <p className="text-muted-foreground mt-1 text-sm">Manage GPU servers, monitor health, and configure service endpoints.</p>
        </div>
        <Button onClick={() => { setEditServer(null); setShowForm(true); }} className="bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-2" />Add Server
        </Button>
      </div>

      {loading ? <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
        <div className="space-y-3">
          {servers.map(s => (
            <Card key={s.server_id} className="bg-card border-border/50">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Server className="w-4 h-4 text-muted-foreground" />
                      <span className="font-bold text-sm">{s.display_name || s.server_id}</span>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[s.status] || ''}`}>{s.status}</Badge>
                      <Badge variant="outline" className="text-[10px]">{s.server_role}</Badge>
                      {s.server_id && <span className="text-[10px] text-muted-foreground font-mono">{s.server_id}</span>}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                      <span>URL: <span className="font-mono">{s.base_url}</span></span>
                      <span>Queue: <span className="font-mono">{s.celery_queue_name}</span></span>
                      <span>Customers: {s.customer_count}</span>
                      {s.assigned_customer_id && <span>Assigned: <span className="font-mono">{s.assigned_customer_id}</span></span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className={`${HEALTH_COLORS[s.last_health_status] || 'text-muted-foreground'}`}>
                        {s.last_health_status === 'ok' ? <Check className="w-3 h-3 inline mr-0.5" /> : s.last_health_status === 'fail' ? <X className="w-3 h-3 inline mr-0.5" /> : null}
                        Health: {s.last_health_status || 'Unknown'}
                      </span>
                      {s.last_health_latency_ms != null && <span className="text-muted-foreground">{s.last_health_latency_ms}ms</span>}
                      <span className="text-muted-foreground">Checked: {timeAgo(s.last_health_check)}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleTest(s.server_id)} disabled={testing[s.server_id] === 'loading'}>
                      {testing[s.server_id] === 'loading' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3 mr-1" />}Test
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditServer(s); setShowForm(true); }}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-destructive border-destructive/30" onClick={() => handleDelete(s.server_id, s.customer_count)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {servers.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No servers configured.</p>}
        </div>
      )}

      <ServerFormDialog open={showForm} onClose={() => setShowForm(false)} server={editServer} onSaved={load} />
    </div>
  );
}

function ServerFormDialog({ open, onClose, server, onSaved }) {
  const isEdit = !!server;
  const [form, setForm] = useState({});
  const [endpoints, setEndpoints] = useState({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (server) {
      setForm({
        server_id: server.server_id || '',
        display_name: server.display_name || '',
        server_role: server.server_role || 'shared',
        status: server.status || 'provisioning',
        base_url: server.base_url || '',
        wireguard_ip: server.wireguard_ip || '',
        celery_queue_name: server.celery_queue_name || '',
        assigned_customer_id: server.assigned_customer_id || '',
        internal_api_key: '',
      });
      // Build endpoints from existing — clear encrypted markers for display
      const eps = {};
      Object.entries(server.service_endpoints || {}).forEach(([svcId, svc]) => {
        eps[svcId] = {};
        Object.entries(svc).forEach(([k, v]) => {
          if (v === '***ENCRYPTED***') eps[svcId][k] = '';
          else eps[svcId][k] = v || '';
        });
      });
      setEndpoints(eps);
    } else {
      setForm({ server_id: '', display_name: '', server_role: 'shared', status: 'provisioning', base_url: '', wireguard_ip: '', celery_queue_name: '', assigned_customer_id: '', internal_api_key: '' });
      setEndpoints({});
    }
  }, [server, open]);

  const updateForm = (key, val) => {
    setForm(f => {
      const n = { ...f, [key]: val };
      if (key === 'server_id' && !isEdit) n.celery_queue_name = `queue-${val}`;
      return n;
    });
  };

  const updateEndpoint = (svcId, field, val) => {
    setEndpoints(e => ({ ...e, [svcId]: { ...(e[svcId] || {}), [field]: val } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Only include non-empty credential fields
      if (!payload.internal_api_key) delete payload.internal_api_key;
      // Build service_endpoints with only changed fields
      const eps = {};
      Object.entries(endpoints).forEach(([svcId, svc]) => {
        const clean = {};
        Object.entries(svc).forEach(([k, v]) => { if (v) clean[k] = v; });
        if (Object.keys(clean).length > 0) eps[svcId] = clean;
      });
      if (Object.keys(eps).length > 0) payload.service_endpoints = eps;

      if (isEdit) {
        await api.patch(`/admin/servers/${form.server_id}`, payload);
        toast.success('Server updated');
      } else {
        await api.post('/admin/servers', payload);
        toast.success('Server created');
      }
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit: ${form.server_id}` : 'Add New Server'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Server ID</Label>
              <Input className="h-8 text-xs font-mono" value={form.server_id} onChange={e => updateForm('server_id', e.target.value)} disabled={isEdit} placeholder="gpu-shared-01" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Display Name</Label>
              <Input className="h-8 text-xs" value={form.display_name} onChange={e => updateForm('display_name', e.target.value)} placeholder="GPU Shared 01" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Role</Label>
              <Select value={form.server_role} onValueChange={v => updateForm('server_role', v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={v => updateForm('status', v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Base URL</Label>
              <Input className="h-8 text-xs font-mono" value={form.base_url} onChange={e => updateForm('base_url', e.target.value)} placeholder="https://api.tensornex.cloud" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">WireGuard IP</Label>
              <Input className="h-8 text-xs font-mono" value={form.wireguard_ip} onChange={e => updateForm('wireguard_ip', e.target.value)} placeholder="10.99.0.1" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Celery Queue Name</Label>
              <Input className="h-8 text-xs font-mono" value={form.celery_queue_name} onChange={e => updateForm('celery_queue_name', e.target.value)} placeholder="queue-gpu-shared-01" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Internal API Key</Label>
              <Input type="password" className="h-8 text-xs font-mono" value={form.internal_api_key} onChange={e => updateForm('internal_api_key', e.target.value)} placeholder={isEdit ? '(leave empty to keep existing)' : 'Enter key'} />
            </div>
            {form.server_role === 'priority-dedicated' && (
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Assigned Customer ID</Label>
                <Input className="h-8 text-xs font-mono" value={form.assigned_customer_id} onChange={e => updateForm('assigned_customer_id', e.target.value)} placeholder="MongoDB ObjectId of dedicated customer" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Service Endpoints</p>
            {Object.entries(SERVICE_FIELDS).map(([svcId, fields]) => (
              <div key={svcId} className="border border-border/30 rounded-lg">
                <button className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-secondary/20" onClick={() => setExpanded(expanded === svcId ? null : svcId)}>
                  <span className="font-medium">{svcId}</span>
                  {expanded === svcId ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {expanded === svcId && (
                  <div className="px-3 pb-3 space-y-2">
                    {fields.map(f => (
                      <div key={f.key} className="flex items-center gap-2">
                        <Label className="text-[10px] w-20 shrink-0">{f.label}</Label>
                        <Input type={f.type} className="h-7 text-xs font-mono" value={endpoints[svcId]?.[f.key] || ''} onChange={e => updateEndpoint(svcId, f.key, e.target.value)} placeholder={isEdit && f.type === 'password' ? '(keep existing)' : ''} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEdit ? 'Update Server' : 'Create Server'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
